/**
 * DataUsageInfoPage component
 * Requirements: 10.4 - Display data collection and usage purpose
 */
import {
  Container,
  Typography,
  Paper,
  Box,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SecurityIcon from '@mui/icons-material/Security';
import PersonIcon from '@mui/icons-material/Person';

interface InfoSection {
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const infoSections: InfoSection[] = [
  {
    title: '収集するデータの種類',
    icon: <StorageIcon color="primary" />,
    content: (
      <Box component="ul" sx={{ mt: 1, mb: 0 }}>
        <li>
          <Typography variant="body2">
            <strong>個人情報:</strong> メールアドレス、氏名
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>回答データ:</strong> 質問への回答内容
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>分析結果:</strong> 思考傾向スコアと判定パターン
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>利用情報:</strong> アクセス日時、セッション情報
          </Typography>
        </li>
      </Box>
    ),
  },
  {
    title: 'データの使用目的',
    icon: <AssessmentIcon color="primary" />,
    content: (
      <Box component="ul" sx={{ mt: 1, mb: 0 }}>
        <li>
          <Typography variant="body2">
            <strong>思考傾向の分析:</strong>{' '}
            回答データをもとに思考パターンを分析し、結果を提供します
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>履歴管理:</strong>{' '}
            過去の分析結果を保存し、時系列での変化を把握できます
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>組織分析:</strong>{' '}
            許可された範囲で部門・全社の傾向を集計します
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>サービス改善:</strong>{' '}
            匿名化されたデータを使用してサービスを改善します
          </Typography>
        </li>
      </Box>
    ),
  },
  {
    title: 'データの保護',
    icon: <SecurityIcon color="primary" />,
    content: (
      <Box component="ul" sx={{ mt: 1, mb: 0 }}>
        <li>
          <Typography variant="body2">
            <strong>暗号化:</strong>{' '}
            すべてのデータは転送時および保管時に暗号化されます
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>アクセス制御:</strong>{' '}
            権限に基づく厳格なアクセス制御を実施しています
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>監査ログ:</strong>{' '}
            データアクセスの記録を保持しています
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>定期的な見直し:</strong>{' '}
            セキュリティ対策を定期的に見直しています
          </Typography>
        </li>
      </Box>
    ),
  },
  {
    title: 'ユーザーによる管理',
    icon: <PersonIcon color="primary" />,
    content: (
      <Box component="ul" sx={{ mt: 1, mb: 0 }}>
        <li>
          <Typography variant="body2">
            <strong>データの閲覧:</strong>{' '}
            いつでも自分のデータを確認できます
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>データのエクスポート:</strong>{' '}
            すべての個人データをJSON形式でダウンロードできます
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            <strong>アカウントの削除:</strong>{' '}
            アカウントと関連データを完全に削除できます
          </Typography>
        </li>
      </Box>
    ),
  },
];

export const DataUsageInfoPage = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          データの収集と使用について
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph>
          本サービスでは、より良いサービスを提供するためにデータを収集・使用しています。
          以下に、収集するデータの種類と使用目的について説明します。
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {infoSections.map((section) => (
          <Card key={section.title} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {section.icon}
                <Typography variant="h6">{section.title}</Typography>
              </Box>
              {section.content}
            </CardContent>
          </Card>
        ))}
      </Paper>
    </Container>
  );
};
